"use client";

import { useEffect, useState } from "react";
import LazyMap from "@/components/maps/LazyMap";
import { Section } from "@/components/ui/Section";
import { SectionHighlight } from "@/components/SectionHighlight";
import { CardGrid } from "@/components/CardGrid";
import { EventCard } from "@/components/cards/EventCard";
import { sagraToEventCard } from "@/lib/card-mappers";
import { getNearbyByRadiusAndHorizonClient } from "@/lib/nearby-client";
import type { SagraListing } from "@/types/sagra";
import type { NearbyEvent } from "@/types/nearby";
import { toNearbyEventFromAny } from "@/lib/nearby-utils";
import { computeDateFlags } from "@/lib/date-flags";
import { haversineDistance } from "@/lib/geo";

type Props = {
  center: { lat: number; lng: number } | null;
  excludeId?: string;
  radiusKm?: number; // default 100
  daysAhead?: number; // default 60
  listLimit?: number; // default 12
  title?: string;
  subtitle?: string;
  showDateRange?: boolean; // default true
  hideHeader?: boolean; // default false
};

export default function NearbySectionClient({
  center,
  excludeId,
  radiusKm: initialRadiusKm = 100,
  daysAhead: initialDaysAhead = 60,
  listLimit = 12,
  title = "Sagre nelle vicinanze",
  showDateRange = true,
  hideHeader = false,
}: Props) {
  const [raw, setRaw] = useState<SagraListing[]>([]);
  const [loading, setLoading] = useState(true);
  // Stato interno per raggio e giorni, aggiornato dai controlli della mappa
  const [currentRadiusKm, setCurrentRadiusKm] = useState(initialRadiusKm);
  const [currentStartDays, setCurrentStartDays] = useState(0);
  const [currentEndDays, setCurrentEndDays] = useState(initialDaysAhead);

  // Calcola daysAhead effettivo dai giorni selezionati
  const effectiveDaysAhead = currentEndDays - currentStartDays;

  // Aggiorna subtitle dinamicamente
  const subtitle = `Entro ${currentRadiusKm} km nei prossimi ${effectiveDaysAhead} giorni`;

  // Carica i dati UNA VOLTA con range ampio (solo quando cambia raggio o centro)
  // Non ricaricare quando cambiano solo le date - filtra i dati già caricati
  useEffect(() => {
    if (!center) {
      setLoading(false);
      return;
    }

    setLoading(true);
    // Carica sempre con daysAhead iniziale (range ampio) per avere tutti i dati
    // Poi filtriamo lato client per il range date selezionato
    getNearbyByRadiusAndHorizonClient({
      center,
      radiusKm: currentRadiusKm,
      daysAhead: initialDaysAhead, // Usa sempre il range massimo
      excludeId,
    })
      .then((data) => {
        setRaw(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [center, currentRadiusKm, excludeId, initialDaysAhead]);

  if (!center) return null;

  // Lista SEO per le card (SagraListing coerente) - filtra per raggio e giorni selezionati
  // Filtra i dati già caricati lato client (senza ricaricare) per evitare flickering
  const filteredAndSorted = raw
    .map((ev: any) => {
      // Filtra per raggio (distanza)
      const latOk = ev?.lat ?? ev?.location?.geo?.lat;
      const lngOk = ev?.lng ?? ev?.location?.geo?.lng;
      if (typeof latOk !== "number" || typeof lngOk !== "number") return null;
      
      const distanceKm = haversineDistance(
        { lat: center.lat, lon: center.lng },
        { lat: latOk, lon: lngOk }
      );
      if (distanceKm > currentRadiusKm) return null;

      // Filtra per giorni selezionati se showDateRange è true
      if (showDateRange) {
        const date_from = ev?.date_from ?? ev?.startDate;
        const date_to = ev?.date_to ?? ev?.endDate;
        if (date_from) {
          const now = Date.now();
          const startTS = now + currentStartDays * 86400000;
          const endTS = now + currentEndDays * 86400000;
          
          const evStartTS = new Date(date_from).getTime();
          const evEndTS = date_to ? new Date(date_to).getTime() : evStartTS;
          
          // L'evento deve intersecare il range selezionato
          if (!(evEndTS >= startTS && evStartTS <= endTS)) return null;
        }
      }
      
      return { ev, distanceKm };
    })
    .filter((r): r is { ev: any; distanceKm: number } => r !== null)
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, listLimit);

  // Converte in SagraListing e calcola i flag
  const allListings: SagraListing[] = filteredAndSorted.map(({ ev }) => {
    const date_from = ev?.date_from ?? ev?.startDate;
    const date_to = ev?.date_to ?? ev?.endDate;
    const year = ev?.year ?? ev?.currentYear;

    // Calcola sempre i flag dalle date per garantire coerenza
    // Passa initialDaysAhead come maxDaysAhead per estendere isComingSoon fino a 60 giorni
    const computed = computeDateFlags(date_from, date_to, initialDaysAhead);

    // Usa i flag esistenti solo se sono effettivamente true (non undefined/false)
    // Questo permette di preservare flag pre-calcolati validi senza perdere quelli calcolati
    const existingFlags = {
      isToday: ev?.isToday === true ? true : computed.isToday,
      isTomorrow: ev?.isTomorrow === true ? true : computed.isTomorrow,
      isThisWeekend: ev?.isThisWeekend === true ? true : computed.isThisWeekend,
      isThisWeek: ev?.isThisWeek === true ? true : computed.isThisWeek,
      isComingSoon: ev?.isComingSoon === true ? true : computed.isComingSoon,
      isConcluded: ev?.isConcluded === true ? true : computed.isConcluded,
    };

    return {
      id: ev.id,
      slug: ev.slug,
      title: ev.titolo ?? ev.title,
      regione: ev.regione,
      provincia: ev.provincia,
      comune: ev.comune,
      currentYear: year,
      targetUrl: ev.targetUrl ?? "",
      lastUpdated: ev.lastUpdated ?? new Date().toISOString(),
      startDate: date_from,
      endDate: date_to,
      tba: ev?.tba ?? (!date_from && !date_to) ? true : undefined,

      // flags per le card (combinazione di calcolati ed esistenti):
      ...existingFlags,
    };
  });

  // Raggruppa per: Oggi, Weekend, In arrivo (fino a 60 giorni)
  const todayListings = allListings.filter((item) => item.isToday === true);
  const weekendListings = allListings.filter(
    (item) => !item.isToday && item.isThisWeekend === true
  );
  // In arrivo: eventi futuri fino a 60 giorni (non oggi, non weekend già inclusi)
  const upcomingListings = allListings.filter(
    (item) => !item.isToday && !item.isThisWeekend && (item.isComingSoon || item.isThisWeek || item.isTomorrow)
  );

  // Pin per la mappa (filtra anche per raggio e date selezionati per coerenza con le card)
  const allPins: NearbyEvent[] = raw
    .filter((ev: any) => {
      // Filtra per raggio
      const latOk = ev?.lat ?? ev?.location?.geo?.lat;
      const lngOk = ev?.lng ?? ev?.location?.geo?.lng;
      if (typeof latOk !== "number" || typeof lngOk !== "number") return false;
      
      const distanceKm = haversineDistance(
        { lat: center.lat, lon: center.lng },
        { lat: latOk, lon: lngOk }
      );
      if (distanceKm > currentRadiusKm) return false;

      // Filtra per giorni selezionati se showDateRange è true
      if (showDateRange) {
        const date_from = ev?.date_from ?? ev?.startDate;
        const date_to = ev?.date_to ?? ev?.endDate;
        if (date_from) {
          const now = Date.now();
          const startTS = now + currentStartDays * 86400000;
          const endTS = now + currentEndDays * 86400000;
          
          const evStartTS = new Date(date_from).getTime();
          const evEndTS = date_to ? new Date(date_to).getTime() : evStartTS;
          
          if (!(evEndTS >= startTS && evStartTS <= endTS)) return false;
        }
      }
      
      return true;
    })
    .map(toNearbyEventFromAny)
    .filter((p): p is NearbyEvent => p !== null);

  return (
    <Section id="sagre-vicine" title={title} subtitle={subtitle} hideHeader={hideHeader}>
      <section>
        <LazyMap
          pins={allPins}
          user={center}
          radiusMeters={currentRadiusKm * 1000}
          height={360}
          showControls={true}
          showDateRange={showDateRange}
          startDays={currentStartDays}
          endDays={currentEndDays}
          maxDays={initialDaysAhead}
          onRadiusChange={(km) => {
            setCurrentRadiusKm(km);
          }}
          onStartDaysChange={(days) => {
            setCurrentStartDays(days);
          }}
          onEndDaysChange={(days) => {
            setCurrentEndDays(days);
          }}
        />
      </section>
      {loading && (
        <div className="text-center py-2 text-xs text-stone-500">Aggiornamento...</div>
      )}

      {/* Sezione: Sagre oggi */}
      <SectionHighlight
        title="Sagre oggi"
        subtitle="Eventi e sagre attive in questo momento"
        isEmpty={todayListings.length === 0}
      >
        <CardGrid>
          {todayListings.slice(0, listLimit).map((item) => (
            <EventCard key={item.id} {...sagraToEventCard(item, { showProvince: true })} />
          ))}
        </CardGrid>
      </SectionHighlight>

      {/* Sezione: Sagre nel weekend */}
      <SectionHighlight
        title="Sagre nel weekend"
        subtitle="Sagre che si svolgono nel fine settimana"
        isEmpty={weekendListings.length === 0}
      >
        <CardGrid>
          {weekendListings.slice(0, listLimit).map((item) => (
            <EventCard key={item.id} {...sagraToEventCard(item, { showProvince: true })} />
          ))}
        </CardGrid>
      </SectionHighlight>

      {/* Sezione: In arrivo */}
      <SectionHighlight
        title="In arrivo"
        subtitle="Sagre nei prossimi 60 giorni"
        isEmpty={upcomingListings.length === 0}
      >
        <CardGrid>
          {upcomingListings.slice(0, listLimit).map((item) => (
            <EventCard key={item.id} {...sagraToEventCard(item, { showProvince: true })} />
          ))}
        </CardGrid>
      </SectionHighlight>

      {!loading && todayListings.length === 0 && weekendListings.length === 0 && upcomingListings.length === 0 && (
        <div className="text-center py-8 text-stone-600">
          Nessuna sagra trovata nel raggio e periodo selezionati.
        </div>
      )}
    </Section>
  );
}

